import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Layers, Move } from 'lucide-react';
import useActionBarLayout from '../hooks/useActionBarLayout';
import { ACTION_BAR_PAGE_COUNT, firstFreeCell, layoutCss, layoutToSave, moveTile, placeTiles, slotAt } from '../utils/actionBarLayout';

const PAGE_FLIP_DELAY_MS = 550;
const readKeys = (rail) => [...rail.querySelectorAll('[data-action-tile]')].map((tile) => tile.getAttribute('data-action-tile'));
// Cells taken by the Arrange and Page buttons, which sit in the rail grid (bottom-right on desktop).
const controlCells = (rail, grid) => [...rail.querySelectorAll('.combat-hud-action-bar__control')].map((control) => {
  const rect = control.getBoundingClientRect();
  return slotAt({ ...grid, x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
}).filter((cell) => cell < grid.columns * grid.rows).sort((a, b) => a - b);
const px = (value, fallback) => { const number = parseFloat(value); return Number.isFinite(number) ? number : fallback; };

// Grid geometry of the rail: columns come from the resolved track list, rows from the height that fits.
function measure(rail) {
  const style = window.getComputedStyle(rail);
  const rect = rail.getBoundingClientRect();
  const gap = px(style.columnGap, 6);
  const tracks = String(style.gridTemplateColumns || '').split(' ').filter((track) => track.endsWith('px'));
  const tileWidth = tracks.length ? px(tracks[0], 72) : 72;
  const tileHeight = px(style.gridAutoRows, 56);
  const left = rect.left + px(style.paddingLeft, 0) + px(style.borderLeftWidth, 0);
  const top = rect.top + px(style.paddingTop, 0) + px(style.borderTopWidth, 0);
  const innerWidth = rect.width - px(style.paddingLeft, 0) - px(style.paddingRight, 0);
  const innerHeight = rect.height - px(style.paddingTop, 0) - px(style.paddingBottom, 0);
  const columns = tracks.length || Math.max(1, Math.floor((innerWidth + gap) / (tileWidth + gap)));
  const rows = Math.max(1, Math.floor((innerHeight + gap) / (tileHeight + gap)));
  return { left, top, columns, rows, tileWidth, tileHeight, gap };
}

/**
 * Action bar with three pages and an arrange mode. Tiles anywhere in the rail (the sheet's resource tiles and
 * the feature tiles portaled into it) are placed by their `data-action-tile` key through generated CSS, so the
 * components that render them keep their own state. Arranging drops a tile into any cell, where it stays (a tile
 * already there swaps places), and holding a tile over the Page button moves it to the next page.
 */
export default function ActionBarPager({ characterId, storedLayout, className = '', children }) {
  const scopeId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const scope = `.combat-hud-action-bar[data-action-bar="${scopeId}"]`;
  const { pages, save } = useActionBarLayout(characterId, storedLayout);
  const railRef = useRef(null), pageButtonRef = useRef(null);
  const [presentKeys, setPresentKeys] = useState([]);
  // Grid size and the cells the Arrange and Page buttons take; measured from the rail's real layout.
  const [grid, setGrid] = useState({ measured: false, columns: 999, cells: 999, blocked: [] });
  const [page, setPage] = useState(0);
  const pageRef = useRef(0);
  pageRef.current = page;
  const [arranging, setArranging] = useState(false);
  const [drag, setDrag] = useState(null);

  // Track which keyed tiles are on the bar and the grid they sit in (before paint, so tiles never jump).
  useLayoutEffect(() => {
    const rail = railRef.current;
    if (!rail) return undefined;
    const update = () => {
      const keys = readKeys(rail);
      setPresentKeys((previous) => (previous.length === keys.length && previous.every((key, index) => key === keys[index]) ? previous : keys));
      const measured = measure(rail);
      const rect = rail.getBoundingClientRect();
      // A bar that is not laid out (hidden panel) has no grid yet: show tiles as they come until it does.
      const next = rect.width > 0 && rect.height > 0
        ? { measured: true, columns: measured.columns, cells: measured.columns * measured.rows, blocked: controlCells(rail, measured) }
        : { measured: false, columns: 999, cells: 999, blocked: [] };
      setGrid((previous) => (JSON.stringify(previous) === JSON.stringify(next) ? previous : next));
    };
    update();
    const mutations = new MutationObserver(update);
    mutations.observe(rail, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-action-tile'] });
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    resize?.observe(rail);
    return () => { mutations.disconnect(); resize?.disconnect(); };
  }, []);

  const blocked = useMemo(() => new Set(grid.blocked), [grid.blocked]);
  const placement = useMemo(() => placeTiles(pages, presentKeys, grid.cells, blocked), [pages, presentKeys, grid.cells, blocked]);
  const shown = drag?.preview || placement;
  const css = useMemo(() => (grid.measured ? layoutCss(scope, shown, page, grid.columns, drag?.key) : ''), [grid.measured, scope, shown, page, grid.columns, drag?.key]);

  useEffect(() => {
    if (!arranging) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') setArranging(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [arranging]);

  // While arranging, tiles never activate: swallow clicks before they reach a tile (including portaled ones).
  useEffect(() => {
    const rail = railRef.current;
    if (!rail || !arranging) return undefined;
    const block = (event) => {
      if (event.target.closest?.('.combat-hud-action-bar__control')) return;
      event.preventDefault(); event.stopPropagation();
    };
    rail.addEventListener('click', block, true);
    return () => rail.removeEventListener('click', block, true);
  }, [arranging]);

  const dragRef = useRef(null);
  dragRef.current = drag;
  const finish = useCallback((commit) => {
    const current = dragRef.current;
    if (!current) return;
    clearTimeout(current.flipTimer);
    current.ghost?.remove();
    setDrag(null);
    if (commit && current.preview && JSON.stringify(current.preview) !== JSON.stringify(current.base)) {
      void save(layoutToSave(pages, current.preview));
    }
  }, [pages, save]);
  useEffect(() => () => { clearTimeout(dragRef.current?.flipTimer); dragRef.current?.ghost?.remove(); }, []);

  const onPointerDown = (event) => {
    if (!arranging || !grid.measured || event.button > 0) return;
    const rail = railRef.current, root = rail;
    const tile = [...rail.querySelectorAll('[data-action-tile]')].find((element) => {
      const rect = element.getBoundingClientRect();
      return rect.width > 0 && event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    });
    if (!tile) return;
    event.preventDefault();
    rail.setPointerCapture?.(event.pointerId);
    const rect = tile.getBoundingClientRect(), rootRect = root.getBoundingClientRect();
    const ghost = tile.cloneNode(true);
    ghost.removeAttribute('data-action-tile');
    ghost.setAttribute('aria-hidden', 'true');
    ghost.classList.add('combat-hud-action-bar__ghost');
    Object.assign(ghost.style, { width: `${rect.width}px`, height: `${rect.height}px`, left: `${rect.left - rootRect.left}px`, top: `${rect.top - rootRect.top}px` });
    root.appendChild(ghost);
    setDrag({ key: tile.getAttribute('data-action-tile'), ghost, base: placement, preview: placement, offsetX: event.clientX - rect.left, offsetY: event.clientY - rect.top, flipTimer: null, flipArmed: false });
  };

  const onPointerMove = (event) => {
    const current = dragRef.current;
    if (!current) return;
    const rootRect = railRef.current.getBoundingClientRect();
    current.ghost.style.left = `${event.clientX - rootRect.left - current.offsetX + railRef.current.scrollLeft}px`;
    current.ghost.style.top = `${event.clientY - rootRect.top - current.offsetY + railRef.current.scrollTop}px`;
    const buttonRect = pageButtonRef.current?.getBoundingClientRect();
    const overPageButton = buttonRect && event.clientX >= buttonRect.left && event.clientX <= buttonRect.right && event.clientY >= buttonRect.top && event.clientY <= buttonRect.bottom;
    if (overPageButton) {
      // Holding a tile over the page button turns the page, like dragging to the edge of a home screen.
      if (!current.flipArmed) {
        current.flipArmed = true;
        current.flipTimer = setTimeout(() => {
          const live = dragRef.current;
          if (!live) return;
          live.flipArmed = false;
          const next = (pageRef.current + 1) % ACTION_BAR_PAGE_COUNT;
          setPage(next);
          setDrag((state) => {
            if (!state) return state;
            const without = state.preview[next].map((key) => (key === state.key ? null : key));
            const cell = firstFreeCell(without, grid.cells, blocked);
            return cell < 0 ? state : { ...state, preview: moveTile(state.base, state.key, next, cell) };
          });
        }, PAGE_FLIP_DELAY_MS);
      }
      return;
    }
    clearTimeout(current.flipTimer);
    current.flipArmed = false;
    const rail = railRef.current, railRect = rail.getBoundingClientRect();
    if (event.clientX < railRect.left || event.clientX > railRect.right || event.clientY < railRect.top || event.clientY > railRect.bottom) return;
    const measured = measure(rail);
    const cell = slotAt({ x: event.clientX, y: event.clientY, ...measured });
    if (cell >= grid.cells || blocked.has(cell)) return;
    // Always from the layout at drag start, so a tile passed over is never left displaced.
    const next = moveTile(current.base, current.key, page, cell);
    if (JSON.stringify(next) !== JSON.stringify(current.preview)) setDrag((state) => state && { ...state, preview: next });
  };

  const onPointerUp = (event) => {
    if (!dragRef.current) return;
    const rect = railRef.current.getBoundingClientRect();
    finish(event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom);
  };

  // Native listeners: many tiles are portaled in from other components, and React delivers their events through
  // those components rather than through this rail.
  const handlers = useRef({});
  handlers.current = { down: onPointerDown, move: onPointerMove, up: onPointerUp, cancel: () => finish(false) };
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return undefined;
    const listeners = { pointerdown: (event) => handlers.current.down(event), pointermove: (event) => handlers.current.move(event),
      pointerup: (event) => handlers.current.up(event), pointercancel: () => handlers.current.cancel() };
    Object.entries(listeners).forEach(([type, listener]) => rail.addEventListener(type, listener));
    return () => Object.entries(listeners).forEach(([type, listener]) => rail.removeEventListener(type, listener));
  }, []);

  // While arranging, empty cells on the page are outlined so the grid tiles move within is visible.
  const emptySlots = arranging && grid.measured ? Math.max(0, grid.cells - blocked.size - (shown[page] || []).filter((key, cell) => key && cell < grid.cells).length) : 0;

  return (
    <div
      ref={railRef}
      className={`combat-hud-dock__resource-rail combat-hud-action-bar ${className}`}
      data-action-bar={scopeId}
      data-arranging={arranging || undefined}
      // In combat the HUD lets clicks through to the map except on controls; the bar must take the drag itself.
      data-allow-pointer-events={arranging ? 'true' : undefined}
      aria-label={arranging ? 'Action bar: drag tiles to arrange them' : 'Class resources'}
    >
      <style>{css}</style>
      {children}
      {Array.from({ length: emptySlots }, (_, index) => <span key={`slot-${index}`} className="combat-hud-action-bar__slot" aria-hidden="true" />)}
      <button
        type="button"
        className="combat-hud-resource-tile combat-hud-feature-action-tile combat-hud-action-bar__control combat-hud-action-bar__control--arrange"
        aria-pressed={arranging}
        aria-label={arranging ? 'Done arranging the action bar' : 'Arrange the action bar'}
        title={arranging ? 'Done arranging (Esc)' : 'Arrange: drag tiles where you want them'}
        onClick={() => { finish(false); setArranging((value) => !value); }}
      >
        <span className="combat-hud-resource-tile__icon" aria-hidden="true"><Move size={18} /></span>
        <span className="combat-hud-resource-tile__name">{arranging ? 'Done' : 'Arrange'}</span>
      </button>
      <button
        ref={pageButtonRef}
        type="button"
        className="combat-hud-resource-tile combat-hud-feature-action-tile combat-hud-action-bar__control combat-hud-action-bar__control--page"
        aria-label={`Action bar page ${page + 1} of ${ACTION_BAR_PAGE_COUNT}. Show the next page.`}
        title={arranging ? 'Next page (hold a tile here to move it to the next page)' : 'Next page'}
        onClick={() => setPage((value) => (value + 1) % ACTION_BAR_PAGE_COUNT)}
      >
        <span className="combat-hud-resource-tile__icon" aria-hidden="true"><Layers size={18} /></span>
        <span className="combat-hud-resource-tile__name">Page {page + 1}/{ACTION_BAR_PAGE_COUNT}</span>
      </button>
    </div>
  );
}
